/**
 * ClinicSlotSelector
 * Grid of time-slot chips for "Hora Marcada" service type.
 * Fetches virtual slots from the backend and lets the user pick one.
 */

import React, { useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, Typography, Tooltip } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import appointmentService from '../../../services/appointmentService';
import type { ClinicScheduleSlot } from '../../../types/appointment';

interface ClinicSlotSelectorProps {
  clinicId: number;
  date: string;
  value: string | null; // "HH:MM"
  onChange: (time: string | null) => void;
}

export const ClinicSlotSelector: React.FC<ClinicSlotSelectorProps> = ({
  clinicId,
  date,
  value,
  onChange,
}) => {
  const [slots, setSlots] = useState<ClinicScheduleSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clinicId || !date) {
      setSlots([]);
      return;
    }
    let cancelled = false;

    const fetchSlots = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await appointmentService.getClinicScheduleSlots(clinicId, date);
        if (!cancelled) setSlots(data);
      } catch (err) {
        console.error('Error loading clinic schedule slots:', err);
        if (!cancelled) setError('Erro ao carregar horários');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSlots();
    return () => { cancelled = true; };
  }, [clinicId, date]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Carregando horários...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 1 }}>
        {error}
      </Typography>
    );
  }

  if (slots.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        Nenhum horário disponível para esta data.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {slots.map((slot) => {
        const isSelected = value === slot.time;
        const isFull = slot.available <= 0;

        return (
          <Tooltip
            key={slot.time}
            title={isFull ? 'Horário lotado' : `${slot.available}/${slot.capacity} vaga${slot.available !== 1 ? 's' : ''}`}
          >
            <span>
              <Chip
                icon={<AccessTime />}
                label={slot.time}
                variant={isSelected ? 'filled' : 'outlined'}
                color={isSelected ? 'primary' : isFull ? 'default' : 'success'}
                disabled={isFull}
                onClick={() => {
                  if (!isFull) {
                    onChange(isSelected ? null : slot.time);
                  }
                }}
                sx={{
                  fontWeight: isSelected ? 700 : 400,
                  opacity: isFull ? 0.5 : 1,
                }}
              />
            </span>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default ClinicSlotSelector;
